import { useAppBridge } from '@shopify/app-bridge-react';
import {
    LegacyCard,
    Button,
    ButtonGroup,
    FormLayout,
    TextField, Modal, BlockStack, Text, Checkbox,
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast } from '@shopify/app-bridge/actions';
import Api from '../../helpers/api';
import formatErrors from '../../helpers/format_errors';

function FormSection ({ show, accountNumber, setAccountNumber, errors }) {
    if (! show) {
        return null;
    }

    return (
        <div style={{marginBottom: '2rem'}}>
            <FormLayout>
                <TextField label="Account Number"
                           value={accountNumber}
                           onChange={setAccountNumber}
                           autoComplete={'on'}
                           error={errors.account_number} />
            </FormLayout>
        </div>
    );
}

export default function ApproveSection ({ customer, setCustomer }) {
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const app = useAppBridge();
    const [modalActive, setModalActive] = useState(false);

    const [selectedFields, setSelectedFields] = useState({});

    const sections = [
        {
            name: "Business Information",
            fields: [
                {key: "company_name", name: "Company Name"},
                {key: "referred_by", name: "Referred by"},
                {key: "types_of_business", name: "Types of Business"},
            ]
        }, {
            name: "Business Contact Information",
            fields: [
                {key: "websites", name: "Relevant Websites and Social Media"},
                {key: "contact_name", name: "Contact Name"},
                {key: "alt_contact_name", name: "Alternative Contact Name"},
                {key: "email", name: "Email"},
                {key: "business_address", name: "Business Address"},
                {key: "years_in_business", name: "Years in Business"},
                {key: "years_at_current_address", name: "Years at Current Address"},
                {key: "business_license", name: "Business License"},
                {key: "business_license_copy", name: "Business License Copy"},
                {key: "state_sales_tax_resale", name: "(California Customers) State Sales Tax Resale #"},
                {key: "state_sales_tax_resale_copy", name: "(California Customers) State Sales Tax Resale Copy"},
                {key: "preferred_carrier", name: "Preferred Carrier"},
                {key: "airport_code", name: "Airport Code"},
            ]
        }, {
            name: "Owner Contact Information",
            fields: [
                {key: "full_name", name: "Full Name"},
                {key: "owner_address", name: "Owner Address"},
                {key: "home_phone", name: "Home Phone Number"},
                {key: "cell_phone", name: "Mobile Phone Number"},
                {key: "fax", name: "Fax"},
                {key: "alt_phone", name: "Alternative Phone"},
            ]
        }, {
            name: "Proofs",
            fields: [
                {key: "proof_1", name: "Proof #1"},
                {key: "proof_2", name: "Proof #2"},
                {key: "proof_3", name: "Proof #3"},
                {key: "proof_4", name: "Proof #4"},
            ]
        }
    ];

    useEffect(() => {
        setLoading(false);
    }, [customer]);

    const handleErrorResponse = error => {
        if ( error?.response?.status == 422 ) {
            setErrors(formatErrors(error.response.data));
        } else {
            throw error;
        }
    }

    const changeStatus = useCallback((status, message) => {
        setLoading(true);

        Api.changeCustomerStatus(customer.id, status)
            .then(response => {
                Toast.create(app, { message: message, duration: 3000 })
                    .dispatch(Toast.Action.SHOW);
                history.goBack()
            })
            .catch(handleErrorResponse)
            .finally(() => setLoading(false))

    }, [customer]);

    const updateField = useCallback((key, val) => {
        setSelectedFields({
            ...selectedFields,
            [key]: val,
        })
    }, [selectedFields])

    const requestData = useCallback(() => {
        setLoading(true);

        Api.requestMoreData(customer.id, {
            fields: selectedFields
        })
            .then(response => {
                Toast.create(app, { message: "Request sent!", duration: 3000 })
                    .dispatch(Toast.Action.SHOW);
                setModalActive(false);
                setSelectedFields({});
            })
            .catch(handleErrorResponse)
            .finally(() => setLoading(false))

    }, [selectedFields]);

    return (
        <LegacyCard sectioned>
            <ButtonGroup>
                <Button variant="primary" tone="critical" onClick={() => changeStatus('rejected', 'Customer was rejected')} disabled={loading || customer?.status === 'rejected'}>Reject</Button>
                <Button variant="primary" onClick={() => changeStatus('in_progress', 'Customer marked as in progress')} disabled={loading || customer?.status === 'in_progress'}>In Progress</Button>
                <Button variant="primary" tone={'success'} onClick={() => changeStatus('approved', 'Customer was approved')} disabled={loading}>Approve</Button>
                <Button variant="primary" onClick={() => {setModalActive(true)}} disabled={loading}>Request More Information</Button>
            </ButtonGroup>

            <Modal
                open={modalActive}
                title={"Request More Information"}
                onClose={() => setModalActive(false)}
                primaryAction={{
                    content: 'Request',
                    disabled: loading,
                    onAction() {
                        requestData();
                    }
                }}
            >
                <Modal.Section>
                    { sections.map(section => (<BlockStack gap={'200'}>
                        <Text variant="headingXl" as="h4">{section.name}</Text>
                        {section.fields.map(field => (
                            <Checkbox label={field.name} checked={selectedFields[field.key] ?? false} onChange={val => updateField(field.key, val)} />
                        ))}
                    </BlockStack>))}
                </Modal.Section>
            </Modal>
        </LegacyCard>
    );
}
